import React, { useId } from "react";
import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useDiscountPresets } from "../../../../../context/DiscountPresetsContext";
import { DiscountPreset } from "../../../../../types/discounts";
import { formatDiscountOff } from "../../../../../utils/discountFormatting";
import { DiscountableLine } from "../../../../../utils/lineItemDiscounts";
import surfaceStyles from "../../../../common/surface/surface.module.css";
import styles from "./QuickDiscountsSection.module.css";

export interface QuickDiscountsSectionProps {
  /** The ticket's / cart's discountable lines. No lines → nothing to
   *  discount, so the section hides. */
  lines: DiscountableLine[];
  /** "+ Add": open the apply-discount modal with `preset` preselected on
   *  every line. */
  onAdd: (preset: DiscountPreset) => void;
  disabled?: boolean;
  /** Draw a dashed divider above the section, for hosts that don't provide
   *  one (Quick Sale's placeholder; DashedActionRow's footer has its own). */
  withDivider?: boolean;
}

/**
 * "Quick add" in the Appointment Summary / Quick Sale: the location's
 * up-to-three quick discounts (Settings → Discounts), in their configured
 * order, one row each (name, "10% off", "+ Add") inside the "No discount
 * applied" box. They are shortcuts for the first discount only: once any
 * discount is on the ticket the section hides (stack more via "Add another
 * discount"). Also renders nothing when none are configured or nothing is
 * discountable.
 */
export default function QuickDiscountsSection({
  lines,
  onAdd,
  disabled = false,
  withDivider = false,
}: QuickDiscountsSectionProps) {
  const { quickDiscounts } = useDiscountPresets();
  const headingId = useId();

  const hasDiscount = lines.some((line) => line.discounts.length > 0);
  if (quickDiscounts.length === 0 || lines.length === 0 || hasDiscount) {
    return null;
  }

  return (
    <Box
      component="section"
      className={styles.section}
      data-divider={withDivider || undefined}
      aria-labelledby={headingId}
      data-testid="quick-discounts-section"
    >
      <Text id={headingId} className={surfaceStyles.eyebrow}>
        Quick add
      </Text>
      <Stack gap={10} mt={10}>
        {quickDiscounts.map((preset) => (
          <Group key={preset.id} gap="sm" wrap="nowrap">
            <Box flex={1} miw={0}>
              <Text size="sm" fw={500} className={styles.name}>
                {preset.name}
              </Text>
              <Text fz={12} c="dimmed">
                {formatDiscountOff(preset.discount_type, preset.amount)}
              </Text>
            </Box>
            <Button
              size="compact-sm"
              fz={12}
              fw={600}
              classNames={{
                root: styles.addButton,
                section: styles.addButtonIcon,
              }}
              leftSection={<IconPlus size={12} stroke={2.4} />}
              aria-label={`Add ${preset.name} discount`}
              disabled={disabled}
              onClick={() => onAdd(preset)}
            >
              Add
            </Button>
          </Group>
        ))}
      </Stack>
    </Box>
  );
}
